import React from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, GitBranch, Radar, AlertTriangle, ScanLine, BellRing, Lock, Globe, ArrowRight } from "lucide-react";

const sections = [
  {
    icon: ShieldCheck,
    title: "DMARC Intelligence",
    summary: "Aggregate report ingestion, alignment monitoring, enforcement readiness, spoofing visibility.",
    bullets: ["Per-IP alignment scoring", "Forensic correlation", "Policy drift detection", "Quarantine/reject readiness"],
  },
  {
    icon: GitBranch,
    title: "SPF Optimization",
    summary: "Lookup depth analysis, include-chain explosion, flattening guidance, DNS resiliency.",
    bullets: ["DNS lookup explosion graph", "Flatten-safe optimization", "Include-chain mapping", "Silent breakage alerts"],
  },
  {
    icon: Radar,
    title: "Sender Discovery",
    summary: "SaaS identification, unknown sender detection, third-party classification, vendor governance.",
    bullets: ["IP → vendor fingerprinting", "Shadow IT exposure", "Vendor lifecycle tracking", "Sender alignment scoring"],
  },
  {
    icon: AlertTriangle,
    title: "Threat Detection",
    summary: "Spoofing spikes, auth anomalies, failing infrastructure, suspicious senders.",
    bullets: ["Anomaly baselining", "Geo + ASN threat enrichment", "Spoofing campaign clustering", "Heatmap dashboards"],
  },
  {
    icon: ScanLine,
    title: "BIMI Readiness",
    summary: "Logo validation, enforcement prerequisites, brand trust signals.",
    bullets: ["VMC validation", "Logo asset linting", "DMARC-at-reject precheck", "Inbox preview"],
  },
  {
    icon: Lock,
    title: "SSL / TLS Posture",
    summary: "MTA-STS enforcement, TLS-RPT ingestion, DANE/TLSA validation, MX certificate expiry monitoring.",
    bullets: ["MTA-STS policy linting", "TLS-RPT report parsing", "DANE / TLSA validation", "Cert-expiry alerting"],
  },
  {
    icon: Globe,
    title: "Domain Reputation",
    summary: "Continuous blocklist exposure, sender-reputation scoring, Google Postmaster and Microsoft SNDS signals.",
    bullets: ["Spamhaus / Barracuda / SURBL", "Google Postmaster ingestion", "Microsoft SNDS reputation", "Time-series reputation"],
  },
  {
    icon: BellRing,
    title: "Alerts & Automation",
    summary: "Real-time notifications, escalation workflows, policy-change alerts.",
    bullets: ["Slack / Teams / email", "Escalation tiers", "Quiet hours & severity routing", "Webhook delivery"],
  },
];

export default function Product() {
  return (
    <div>
      <section className="pt-20 pb-16">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-orange-600 mb-5">Product</div>
          <h1 className="font-display text-5xl lg:text-6xl font-bold tracking-tight leading-[1.02] ink">
            Eight surfaces.<br /><span className="font-serif italic font-normal">One trust layer.</span>
          </h1>
          <p className="mt-7 text-lg ink-soft leading-relaxed">
            TrustDmarc continuously evaluates every authentication signal, every sender, and every policy boundary across your domain estate.
          </p>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-6xl mx-auto px-6 grid lg:grid-cols-2 gap-5">
          {sections.map(({ icon: Icon, title, summary, bullets }, i) => (
            <div key={title} className="panel p-8" data-testid={`product-${title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              <div className="flex items-start justify-between mb-6">
                <div className="h-11 w-11 rounded-lg bg-orange-50 flex items-center justify-center">
                  <Icon className="h-5 w-5 text-orange-600" strokeWidth={2} />
                </div>
                <div className="text-xs font-mono ink-muted">{String(i + 1).padStart(2, "0")}</div>
              </div>
              <h3 className="font-display text-2xl font-bold ink">{title}</h3>
              <p className="mt-3 text-sm ink-soft leading-relaxed">{summary}</p>
              <ul className="mt-5 grid grid-cols-2 gap-x-4 gap-y-2">
                {bullets.map((b) => (
                  <li key={b} className="text-xs ink-soft flex items-start gap-1.5">
                    <span className="text-orange-600 font-bold mt-0.5">›</span>
                    {b}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      <section className="py-32">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <h2 className="font-display text-4xl font-bold tracking-tight ink leading-[1.05]">
            Ready to map every sender?
          </h2>
          <p className="mt-4 ink-soft text-lg">Onboard a domain in under five minutes.</p>
          <Link to="/login" className="btn-accent mt-8" data-testid="product-cta">
            Start free <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
